import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PatientPortalService } from 'src/app/core/services/patient-portal.service';
import Swal from "sweetalert2";

import { loadStripe, Stripe, StripeElements, StripeCardElement } from '@stripe/stripe-js';
import { environment } from "src/environments/environment";

type BrandEnum = "VISA" | "MASTERCARD" | "AMEX" | "DISCOVER" | "OTHER";

@Component({
  selector: 'app-attch-my-card',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './attch-my-card.component.html',
  styleUrl: './attch-my-card.component.scss'
})
export class AttchMyCardComponent implements OnInit, OnDestroy {

  // 🔥 INPUTS
  @Input() patientId!: string;
  @Input() patientName: string = "";
  @Input() setAsDefaultInitial = true;

  // 🔥 STATE
  loading = false;
  cardComplete = false;
  cardError = "";

  // 🔥 FORM
  cardName = "";
  setAsDefault = true;

  // 🔥 STRIPE
  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private cardEl!: StripeCardElement;

  // 🔥 PREVIEW DATA
  brand: BrandEnum = "OTHER";
  private stripeLast4 = "";
  expMonth: number | null = null;
  expYear: number | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private service: PatientPortalService
  ) { }

  // =========================
  // UI HELPERS
  // =========================

  get safeLast4(): string {
    const v = (this.stripeLast4 || "").replace(/\D/g, "").slice(0, 4);
    return v ? v : "••••";
  }

  get safeExpiry(): string {
    if (!this.expMonth || !this.expYear) return "MM/YY";
    const mm = String(this.expMonth).padStart(2, "0");
    const yy = String(this.expYear).slice(-2);
    return `${mm}/${yy}`;
  }

  get safeCvc(): string {
    return "•••";
  }

  private mapStripeBrandToEnum(brand?: string): BrandEnum {
    const b = (brand || "").toLowerCase();
    if (b === "visa") return "VISA";
    if (b === "mastercard") return "MASTERCARD";
    if (b === "amex" || b === "american_express") return "AMEX";
    if (b === "discover") return "DISCOVER";
    return "OTHER";
  }

  // =========================
  // INIT
  // =========================

  async ngOnInit(): Promise<void> {
    this.setAsDefault = !!this.setAsDefaultInitial;

    try {
      if (!environment?.stripePublishableKey) {
        this.cardError = "Stripe key missing";
        return;
      }

      this.stripe = await loadStripe(environment.stripePublishableKey);

      if (!this.stripe) {
        this.cardError = "Stripe failed to load";
        return;
      }

      this.elements = this.stripe.elements();

      this.cardEl = this.elements.create("card", {
        hidePostalCode: true
      });

      setTimeout(() => {
        this.cardEl.mount("#card-element");
      });

      this.cardEl.on("change", (event: any) => {
        this.cardComplete = !!event.complete;
        this.cardError = event?.error?.message || "";

        // 🔥 BRAND
        this.brand = this.mapStripeBrandToEnum(event?.brand);

        if (!this.cardComplete) {
          this.stripeLast4 = "";
          this.expMonth = null;
          this.expYear = null;
        }
      });

    } catch (e: any) {
      this.cardError = e?.message || "Stripe init failed";
    }
  }

  ngOnDestroy(): void {
    try {
      if (this.cardEl) this.cardEl.destroy();
    } catch { }
  }

  // =========================
  // ATTACH CARD (FINAL FIXED)
  // =========================

  async attach(): Promise<void> {

    if (!this.stripe || !this.cardEl) {
      Swal.fire("Stripe not ready", this.cardError || "Init error", "error");
      return;
    }

    if (!this.cardComplete) {
      Swal.fire("Incomplete", "Please enter card details", "warning");
      return;
    }

    this.loading = true;

    try {
      const result = await this.stripe.createPaymentMethod({
        type: "card",
        card: this.cardEl,
        billing_details: {
          name: this.cardName
        }
      });

      if (result.error) {
        this.loading = false;
        Swal.fire("Card Error", result.error.message || "Error", "error");
        return;
      }

      const paymentMethodId = result.paymentMethod?.id;

      if (!paymentMethodId) {
        this.loading = false;
        Swal.fire("Error", "Payment method not created", "error");
        return;
      }

      // 🔥 PREVIEW UPDATE
      const pmCard = result.paymentMethod?.card;

      this.stripeLast4 = pmCard?.last4 || "";
      this.expMonth = pmCard?.exp_month ?? null;
      this.expYear = pmCard?.exp_year ?? null;
      this.brand = this.mapStripeBrandToEnum(pmCard?.brand);

      // ✅ PATIENT API (CORRECT)
      this.service.attachCard({
        paymentMethodId,
        setAsDefault: this.setAsDefault
      }).subscribe({
        next: () => {
          this.loading = false;

          Swal.fire({
            icon: "success",
            title: "Card Attached",
            timer: 1200,
            showConfirmButton: false
          });

          this.activeModal.close({ attached: true });
        },
        error: (err) => {
          this.loading = false;
          Swal.fire("Attach Failed", err?.error?.message || "Error", "error");
        }
      });

    } catch (e: any) {
      this.loading = false;
      Swal.fire("Error", e?.message || "Unexpected error", "error");
    }
  }
}